import React, { useState, useEffect, useCallback } from 'react';
import { G300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { SuccessAutoDismissFlag } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import messages from './messages.tsx';

// eslint-disable-next-line @compiled/no-css-prop-without-css-function, @atlaskit/design-system/consistent-css-prop-usage
const copiedSuccessStyles = {
	color: token('color.background.success.pressed', G300),
};

const getIssueDiscoveryUrl = (issueKeys: string[]): string => {
	if (issueKeys.length > 1) {
		const cardKeys = issueKeys.join(',');
		const jqlSearch = encodeURIComponent(`issueKey in (${cardKeys})`);
		return `/issues/?jql=${jqlSearch}`;
	}
	return `/browse/${issueKeys[0]}`;
};

type Props = {
	originalIssueKey: string;
	issueKeys: string[];
	onDismissed?: (id: string | number) => void;
	id: string | number;
};

export const SplitIssueSuccessFlag = ({ originalIssueKey, issueKeys, onDismissed, id }: Props) => {
	const { formatMessage } = useIntl();
	const [isCopied, setCopied] = useState(false);

	useEffect(() => {
		let timeoutId: ReturnType<typeof setTimeout>;
		if (isCopied) {
			timeoutId = setTimeout(() => {
				setCopied(false);
			}, 2000);
		}
		return () => {
			timeoutId && clearTimeout(timeoutId);
		};
	}, [isCopied]);

	const issueDiscoveryUrl = getIssueDiscoveryUrl(issueKeys);

	const copyToClipboard = useCallback((): void => {
		if (fg('jfp-magma-undefined-navigator')) {
			globalThis.navigator.clipboard.writeText(
				`${globalThis.window.location.origin}${issueDiscoveryUrl}`,
			);
		} else {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			navigator.clipboard.writeText(`${window.location.origin}${issueDiscoveryUrl}`);
		}
		setCopied(true);
	}, [issueDiscoveryUrl]);

	const getFlagTitle = useCallback(() => {
		if (expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)) {
			return issueKeys.length > 1
				? formatMessage(messages.multiplesIssuesCreatedSuccessFlagTitleIssueTermRefresh, {
						issueCount: issueKeys.length,
					})
				: formatMessage(messages.createSuccessFlagTitleIssueTermRefresh, {
						issueKey: issueKeys[0],
					});
		}

		return issueKeys.length > 1
			? formatMessage(messages.multiplesIssuesCreatedSuccessFlagTitle, {
					issueCount: issueKeys.length,
				})
			: formatMessage(messages.createSuccessFlagTitle, { issueKey: issueKeys[0] });
	}, [formatMessage, issueKeys]);

	const getFlagDescription = useCallback(() => {
		if (expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)) {
			return issueKeys.length > 1
				? formatMessage(messages.multipleIssuesCreatedSuccessFlagDescriptionIssueTermRefresh, {
						issueKey: originalIssueKey,
					})
				: formatMessage(messages.createSuccessFlagDescriptionIssueTermRefresh, {
						issueKey: originalIssueKey,
					});
		}

		return issueKeys.length > 1
			? formatMessage(messages.multipleIssuesCreatedSuccessFlagDescription, {
					issueKey: originalIssueKey,
				})
			: formatMessage(messages.createSuccessFlagDescription, { issueKey: originalIssueKey });
	}, [formatMessage, issueKeys, originalIssueKey]);

	const flagActions = [
		{
			content: formatMessage(
				expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
					? messages.viewIssueLinkTextIssueTermRefresh
					: messages.viewIssueLinkText,
				{
					numIssues: issueKeys.length,
				},
			),
			href: issueDiscoveryUrl,
		},
		{
			content: isCopied ? (
				<span
					// @ts-expect-error Property 'css' does not exist on type 'DetailedHTMLProps<HTMLAttributes<HTMLSpanElement>, HTMLSpanElement>'.ts(2322)
					css={copiedSuccessStyles}
				>
					{formatMessage(messages.linkCopied)}
				</span>
			) : (
				formatMessage(messages.copyLink)
			),
			onClick: () => {
				copyToClipboard();
			},
		},
	];

	return (
		<SuccessAutoDismissFlag
			id={id}
			onDismissed={onDismissed}
			title={getFlagTitle()}
			description={getFlagDescription()}
			actions={flagActions}
		/>
	);
};
